import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CreditCard, X, ArrowRight, Mail, Sparkles, Lock } from "lucide-react";
import api from "@/lib/api";

let installed = false;
let openModal = null;

/** Mount once in App.js: <QuotaInterceptor /> */
export function QuotaInterceptor() {
  const [payload, setPayload] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    if (installed) return;
    installed = true;
    api.interceptors.response.use(
      (r) => r,
      (err) => {
        if (err?.response?.status === 402) {
          const detail = err.response.data?.detail;
          if (detail && typeof detail === "object" && detail.code === "quota_exceeded") {
            (openModal && openModal(detail));
          }
        }
        return Promise.reject(err);
      },
    );
  }, []);

  useEffect(() => {
    openModal = (p) => setPayload(p);
    return () => { openModal = null; };
  }, []);

  if (!payload) return null;

  const close = () => setPayload(null);
  const goUpgrade = () => { close(); nav(payload.upgrade_url || "/billing/plans"); };
  const contactSupport = () => {
    window.location.href = "mailto:support@bygglov.se?subject=Önskar diskutera planuppgradering";
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-900/60 backdrop-blur-sm p-4"
         data-testid="quota-modal">
      <div className="w-full max-w-md bg-white border border-zinc-200 rounded-sm p-6 animate-fade-in-up">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 bg-amber-100 text-amber-700 rounded-sm flex items-center justify-center">
            <Lock className="w-5 h-5" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-amber-700">Kvot uppnådd</div>
            <h3 className="text-lg font-medium" style={{ fontFamily: "Outfit" }}>Uppgradera för att fortsätta</h3>
          </div>
          <button onClick={close} className="ml-auto p-1 hover:bg-zinc-100 rounded-sm">
            <X className="w-4 h-4" />
          </button>
        </div>

        <p className="text-sm text-zinc-700 leading-relaxed">{payload.message}</p>

        <div className="mt-5 px-4 py-3 bg-zinc-50 border border-zinc-200 rounded-sm text-sm space-y-1">
          <div className="flex justify-between"><span className="text-zinc-500">Nuvarande plan</span><b className="capitalize">{payload.current_plan}</b></div>
          <div className="flex justify-between"><span className="text-zinc-500">Använt denna månad</span><b>{payload.current_usage} / {payload.limit}</b></div>
        </div>

        {payload.suggested_plan && (
          <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-sm">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-4 h-4 text-blue-700" />
              <span className="text-xs uppercase tracking-wider font-semibold text-blue-700">Rekommenderad uppgradering</span>
            </div>
            <div className="text-base font-medium text-zinc-900">
              {payload.suggested_plan_name} – {Number(payload.suggested_plan_price_sek || 0).toLocaleString("sv-SE")} kr/månad
            </div>
            <div className="text-xs text-zinc-600 mt-1">
              {payload.suggested_plan === "pro" && "Obegränsade ärenden, AI-granskning, e-postvidarebefordran"}
              {payload.suggested_plan === "enterprise" && "Obegränsat allt + SSO/SAML + dedikerad support"}
            </div>
          </div>
        )}

        <div className="mt-6 grid gap-2">
          <button onClick={goUpgrade} data-testid="quota-upgrade"
            className="w-full px-4 py-2.5 bg-blue-700 hover:bg-blue-800 text-white text-sm font-medium rounded-sm inline-flex items-center justify-center gap-2">
            <CreditCard className="w-4 h-4" /> Uppgradera nu <ArrowRight className="w-4 h-4" />
          </button>
          <button onClick={contactSupport}
            className="w-full px-4 py-2 border border-zinc-300 text-zinc-700 hover:bg-zinc-50 text-sm font-medium rounded-sm inline-flex items-center justify-center gap-2">
            <Mail className="w-4 h-4" /> Kontakta sälj för offert
          </button>
          <button onClick={close}
            className="w-full px-4 py-2 text-xs text-zinc-500 hover:text-zinc-700">
            Stäng – jag väntar till nästa månad
          </button>
        </div>

        <p className="mt-4 text-[10px] text-zinc-400 text-center">
          Befintliga ärenden förblir tillgängliga i läs-läge tills planen uppgraderas.
        </p>
      </div>
    </div>
  );
}
